import React, { ChangeEvent, FC, Fragment, ReactNode } from "react";
import { IViewInputControl, ISelectOption } from "../../Interfaces";
import { useStyles } from "./ViewInputControl.styles";
import classNames from "classnames";
import {
  Typography,
  FormLabel,
  TextField,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  TextareaAutosize,
  Switch,
  Autocomplete,
  FormHelperText,
  Box,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DATE_FORMAT, EDITOR_TYPES } from "../../Enums";
import {
  PHONE_MAX_LENGTH,
  fieldHelperText,
  isRequiredField,
  normalisePhoneInput,
} from "./ViewInputControl.rules";
import { observer } from "mobx-react";
import { action } from "mobx";
import moment from "moment";
import MobxReactForm from "mobx-react-form";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/de";
import AsyncAutocomplete from "../CoreUI/AsyncAutocomplete/AsyncAutocomplete";
import { NumericFormatCustom } from "../CoreUI/NumericFormatCustom/NumericFormatCustom";
import { numericFormatter } from "react-number-format";
import FileUploader from "../CoreUI/FileUploader/FileUploader";
import DateRangePicker from "../CoreUI/DateRangePicker/DateRangePicker";
import NumericRangeInput from "../CoreUI/NumericRangeInput/NumericRangeInput";
import AddressAutocomplete from "../CoreUI/AddressAutocomplete/AddressAutocomplete";
interface Props extends IViewInputControl {
  form?: MobxReactForm;
  handleClose?: () => void;
  value?: any;
  onKeyPress?: (e) => void;
  onFieldChange?: (e) => void;
  onButtonClickHandler?: (e) => void;
}

const ViewInputControl: FC<Props> = (props: Props) => {
  const { isEditingMode, classes, form, onValueChange, options, hidden } =
    props;
  const styleClasses = useStyles();
  const inputClass = classNames({
    [styleClasses.flexRow]: true,
    [classes?.flexBlock]: classes?.flexBlock,
    [styleClasses.textArea]: true,
  });

  const getField = (key: string) => form.$(key);
  const fieldValue = (): any => {
    const { type, fieldKey } = props;
    const field = props.field || props.form.$(fieldKey);
    switch (type) {
      case EDITOR_TYPES.TEXT_FIELD:
        // Absence, not falsiness. A numeric field holding a real zero — a pipeline value of 0, a head
        // count of 0 — is a captured value, and `field.value || "-"` reported it as nothing recorded,
        // which is the opposite of what is stored. Same class as bug 5840.
        return field.value === null ||
          field.value === undefined ||
          field.value === ""
          ? "-"
          : field.value;
      case EDITOR_TYPES.DROPDOWN: {
        if (props.multiple && field.value instanceof Array) {
          return field.value || [];
        } else if (props.multiple) {
          return [];
        }
        return field.value || " ";
      }
      case EDITOR_TYPES.AUTO_COMPLETE: {
        if (field.value && !props.multiple) {
          return options.find((x) => field.value === x.value);
        }
        if (props.multiple && field.value instanceof Array) {
          return options.filter((x) => field.value.includes(x.value));
        } else if (props.multiple) {
          return [];
        }

        return null;
      }
      case EDITOR_TYPES.FILE_UPLOADER: {
        if (props.multiple && field.value instanceof Array) {
          return field.value || [];
        } else if (props.multiple) {
          return [];
        }
        return field.value || {};
      }
      default:
        return "-";
    }
  };

  const onValChange = (value: any, fieldKey: string): void => {
    const { type, multiple } = props;
    const field = props.field || props.form.$(fieldKey);
    field.showErrors(true);
    switch (type) {
      case EDITOR_TYPES.AUTO_COMPLETE: {
        let val;
        if (value && multiple) {
          val = value.map((c) => c.value);
        }
        if (value && !multiple) {
          val = value.value || null;
        }
        getField(fieldKey).set(val);
        break;
      }
      default:
        getField(fieldKey).set(value);
    }
    onValueChange(value, fieldKey);
  };

  // const switchHandler = (value: string) => {
  //   onValueChange(value, props.field.name);
  // };

  const readOnlyFieldValue = (): ReactNode => {
    const { classes, isMasked } = props;
    const value = fieldValue();
    const isPresent = value !== null && value !== undefined && value !== "" && value !== "-";
    
    return (
      <>
        <Typography align="left">
          {isMasked && isPresent
            ? numericFormatter(`${value}`, {
                thousandSeparator: true,
                decimalScale: 2,
                fixedDecimalScale: true,
                prefix: "$",
              })
            : value}
        </Typography>
      </>
    );
  };

  // const changeDatePickerValue = (value, date, field, dateTimeFormat) => {
  //   if (date && !moment(date).isValid()) {
  //     field.invalidate(`Invalid date or time (${dateTimeFormat})`);
  //   }
  //   onValueChange(date ? date : "", field.key);
  // };

  const editableContent = (): ReactNode => {
    const {
      classes,
      type,
      isDisabled,
      dateTimeFormat,
      showLabel,
      disabled,
      options,
      multiple,
      isCustom,
      isadornment,
      endAdornment,
      startAdornment,
      minDate,
      maxDate,
      minTime,
      maxTime,
      form,
      handleClose,
      minRows,
      className,
      inputProps,
      onKeyDown,
      onKeyPress,
      onFieldChange,
      onButtonClickHandler,
      dateFormat,
      timeFormat,
      width,
      fieldKey,
      attachmentType,
      apiObservable,
      isMasked,
      disabledOptions,
      customComponent,
      helperText,
      disabledEnd,
      disabledStart,
      stickyOptions,
    } = props;
    const field = props.field || form.$(fieldKey);
    const { hasError, touched, value, placeholder, key, errorSync } = field;

    // field.disabled
    switch (type) {
      case EDITOR_TYPES.DATE:
        return (
          <>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale="en-gb"
            >
              <DatePicker
                sx={{
                  width: width,
                }}
                {...field.bind()}
                label={field.label}
                format={dateFormat}
                value={field.value ? moment(field.value, dateFormat) : null}
                minDate={minDate}
                maxDate={maxDate}
                defaultValue={null}
                disabled={isDisabled || field.disabled}
                onChange={(date) => {
                  // changeDatePickerValue(value, date, field, dateTimeFormat);
                  onValChange(date, key);
                }}
                readOnly={inputProps?.readOnly}
                slotProps={{
                  textField: {
                    error: !!field.error,
                    // A required date carried no asterisk, so slaExpiryDate and the revenue date
                    // looked optional until the save was refused (bug 5668).
                    required: isRequiredField(field),
                    helperText: fieldHelperText(field, helperText),
                  },
                }}
              />
            </LocalizationProvider>
          </>
        );

      case EDITOR_TYPES.TIME:
        return (
          <>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale="en-gb"
            >
              <TimePicker
                sx={{
                  width: width,
                }}
                {...field.bind()}
                label={field.label}
                format={timeFormat}
                onChange={(date) => {
                  onValChange(date, key);
                }}
                minTime={minTime}
                maxTime={maxTime}
                disabled={isDisabled}
                value={field.value || null}
              />
            </LocalizationProvider>
          </>
        );

      case EDITOR_TYPES.RADIO_BUTTON:
        return (
          <>
            <FormControl
              error={!!field.error}
              required={isRequiredField(field)}
            >
              <FormLabel>{field.label}</FormLabel>
              <RadioGroup
                row
                defaultValue={field.value || null}
                value={field.value}
                onChange={({ target }) => {
                  onValChange(target.value, key);
                }}
              >
                {options.map((option, index) => (
                  <Fragment key={index}>
                    <FormControlLabel
                      value={option.value}
                      disabled={isDisabled}
                      control={<Radio />}
                      label={option.label}
                      key={index}
                    />
                  </Fragment>
                ))}
              </RadioGroup>
              {/* The reason, not just a recoloured label (bug 5668). */}
              {field.error && <FormHelperText>{field.error}</FormHelperText>}
            </FormControl>
          </>
        );

      case EDITOR_TYPES.TEXTAREA:
        return (
          <TextareaAutosize
            minRows={minRows}
            placeholder={placeholder}
            onChange={({ target }) => {
              onValChange(target.value, field.key);
            }}
            value={props.value || value || ""}
            className={styleClasses.textArea}
          />
        );

      case EDITOR_TYPES.AUTO_COMPLETE:
        return (
          <>
            <Autocomplete
              value={fieldValue() || null}
              fullWidth
              multiple={multiple}
              onChange={(_, newValue) => {
                onValChange(newValue, key);
              }}
              options={options}
              sx={{ width: { width } }}
              getOptionLabel={(option: ISelectOption) => option.label || ""}
              getOptionKey={(item: ISelectOption) => item.value}
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={field.label}
                  error={!!field.error}
                  required={isRequiredField(field)}
                  helperText={fieldHelperText(field, helperText)}
                />
              )}
              readOnly={inputProps?.readOnly}
              disabled={isDisabled}
              onFocus={() => {
                field.onFocus();
                props.onFocus(key);
              }}
              onBlur={() => {
                field.onBlur();
                props.onBlur(key, value);
              }}
            />
          </>
        );

      case EDITOR_TYPES.AUTO_COMPLETE_ASYNC:
        return (
          <>
            <AsyncAutocomplete
              multiple={multiple}
              label={field.label}
              value={field.value}
              sx={{ width: { width } }}
              readOnly={inputProps?.readOnly}
              onValueChange={(newValue) => {
                onValChange(newValue, key);
              }}
              disabled={isDisabled}
              apiObservable={apiObservable}
              error={!!field.error}
              required={isRequiredField(field)}
              helperText={fieldHelperText(field, helperText)}
              hideValueInOption={props.hideValueInOptions}
              stickyOptions={stickyOptions}
            />
          </>
        );

      case EDITOR_TYPES.CHECKBOX:
        return (
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                checked={field.value}
                name={field.label}
                onChange={({ target }) => {
                  onValChange(target.checked, field.key);
                }}
              />
            }
            label={field.label}
          />
        );

      case EDITOR_TYPES.DROPDOWN:
        return (
          <>
            <FormControl
              fullWidth
              error={!!field.error}
              required={isRequiredField(field)}
            >
              <InputLabel>{showLabel && field.label}</InputLabel>
              <Select
                sx={{
                  width: width,
                }}
                multiple={multiple}
                className={styleClasses.selectBox}
                fullWidth
                value={fieldValue() || []}
                {...field.bind()}
                label={field.label}
                error={!!field.error}
                onChange={({ target }) => {
                  onValChange(target.value, field.key);
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 500 } } }}
                inputProps={inputProps && inputProps}
              >
                {options &&
                  options.length > 0 &&
                  options.map((option, index) => (
                    <MenuItem
                      disabled={
                        disabledOptions &&
                        disabledOptions.some((x) => x.value === option.value)
                      }
                      value={option.value}
                      key={index}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                {options?.length === 0 && <MenuItem value="">Select</MenuItem>}
              </Select>
              <FormHelperText>
                {fieldHelperText(field, helperText)}
              </FormHelperText>
              {/* {(helperText || field.error) && (
              )} */}
            </FormControl>
          </>
        );

      case EDITOR_TYPES.SWITCH:
        return (
          <>
            <FormControl className={styleClasses.switch} fullWidth>
              <FormControlLabel
                disabled={field.disabled}
                // {...field.bind()}
                // value={field.value}
                // className={styleClasses.swichBox}
                control={
                  <Switch
                    checked={Boolean(field.value || false)}
                    onChange={(_, value) => onValChange(value, key)}
                  />
                }
                label={field.label}
                labelPlacement="top"
              />
            </FormControl>
          </>
        );

      case EDITOR_TYPES.FILE_UPLOADER:
        return (
          <>
            <FileUploader
              label={showLabel && field.label}
              onFileChange={(value) => {
                onValChange(value, key);
              }}
              value={fieldValue()}
              multiple={multiple}
              attachmentType={attachmentType ?? null}
              disabled={field.disabled || isDisabled}
            />
          </>
        );

      case EDITOR_TYPES.DATE_RANGE:
        return (
          <>
            <DateRangePicker
              label={field.label}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(dataRange) => {
                onValChange(dataRange, fieldKey);
              }}
              disabledEnd={disabledEnd}
              disabledStart={disabledStart}
              readonly={inputProps?.readOnly}
              value={field.value}
            />
          </>
        );
      case EDITOR_TYPES.NUMERIC_RANGE:
        return (
          <>
            <NumericRangeInput
              label={field.label}
              onChange={(range) => {
                onValChange(range, fieldKey);
              }}
              value={field.value}
              disabled={disabled}
            />
          </>
        );

      case EDITOR_TYPES.ADDRESS_LOOKUP:
        return (
          <AddressAutocomplete
            label={showLabel === false ? undefined : field.label}
            value={value}
            required={isRequiredField(field)}
            disabled={disabled || isDisabled}
            error={!!field.error}
            helperText={field.error || errorSync || helperText}
            placeholder={placeholder}
            countryCode={inputProps?.countryCode}
            onChange={(address) => onValChange(address, key)}
            onPlaceSelected={inputProps?.onPlaceSelected}
          />
        );

      case EDITOR_TYPES.CUSTOM:
        return customComponent;

      // A phone number is captured as typed (bugs 5671, 5675).
      //
      // This was a dial-code widget that reformatted on every keystroke. That swallowed Backspace,
      // Delete and Ctrl+A, so a wrong number could not be corrected; it prefilled +64 whatever the
      // client's country; it read a local 0211234567 as country code +02; and it re-rendered a
      // CSV-imported 0400111222 as "+04 001-112-22" and flagged it invalid. Nothing in the
      // specification asks for dial-code parsing — Mobile/Phone is optional free text (S4.1) — and
      // Site Phone and Head Office Phone have always been plain fields, so this is now consistent
      // with them as well as usable.
      case EDITOR_TYPES.Phone_Number:
        return (
          <TextField
            fullWidth
            label={showLabel === false ? undefined : field.label}
            placeholder={placeholder || "e.g. 021 123 4567"}
            value={props.value || value || ""}
            disabled={isDisabled || field.disabled}
            error={!!field.error}
            required={isRequiredField(field)}
            helperText={fieldHelperText(field, helperText)}
            variant="outlined"
            autoComplete="tel"
            inputProps={{ inputMode: "tel", maxLength: PHONE_MAX_LENGTH }}
            onChange={({ target }) =>
              onValChange(normalisePhoneInput(target.value), key)
            }
            onBlur={() => {
              field.onBlur();
              props.onBlur(key, value);
            }}
            onFocus={() => {
              field.onFocus();
              props.onFocus(key);
            }}
          />
        );
      case EDITOR_TYPES.TEXT_FIELD:
      default:
        const _hasError = Boolean(Boolean(form?.submitted) && !!field.error);

        // A number field that declares a minimum of zero or more cannot hold a negative, so the keys
        // and pastes that would start one are refused at the input rather than let in and rejected
        // afterwards. The rule is read off the declared minimum, so no field changes behaviour unless
        // it has said what its floor is.
        const declaredMin = Number(inputProps?.min);
        const refusesNegative =
          (field.type || "text") === "number" &&
          Number.isFinite(declaredMin) &&
          declaredMin >= 0;

        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
          // "e" and "E" are accepted by number inputs as an exponent, and "1e-3" is negative.
          if (refusesNegative && ["-", "+", "e", "E"].includes(e.key)) {
            e.preventDefault();
            return;
          }
          // Previously accepted as a prop and never wired to anything, so every consumer that passed
          // a key handler had it silently dropped.
          onKeyDown?.(e);
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
          if (refusesNegative && e.clipboardData.getData("text").includes("-"))
            e.preventDefault();
        };

        return (
          <>
            <TextField
              fullWidth
              autoComplete="off"
              {...field.bind()}
              label={showLabel && field.label}
              onChange={action(({ target }: ChangeEvent<HTMLInputElement>) => {
                onValChange(target.value, key);
              })}
              onBlur={() => {
                field.onBlur();
                props.onBlur(key, value);
              }}
              onFocus={() => {
                field.onFocus();
                props.onFocus(key);
              }}
              type={field.type || "text"}
              variant="outlined"
              value={props.value || value || ""}
              placeholder={placeholder || field.label}
              error={!!field.error}
              required={isRequiredField(field)}
              // The message shows under exactly the condition that already turns the field red, so a
              // blocked save always says why instead of colouring a field silently (bug 5703).
              helperText={fieldHelperText(field, helperText)}
              disabled={isDisabled || field.disabled}
              multiline={props.multiline}
              rows={props.rows}
              className={classNames({
                [styleClasses.input]: true,
                [classes?.inputControl]: classes?.inputControl,
              })}
              InputProps={{
                endAdornment: isadornment ? endAdornment : <></>,
                startAdornment: isadornment ? startAdornment : <></>,
                inputComponent: isMasked && (NumericFormatCustom as any),
                inputProps: inputProps && inputProps,
              }}
              onKeyPress={(e) => onKeyPress(e)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
          </>
        );
    }
  };

  const readOnlyContent = (): ReactNode => {
    const { classes, field, showLabel = true, helperText } = props;

    return (
      <>
        {showLabel && <FormLabel>{field.label}</FormLabel>}

        {readOnlyFieldValue()}

        {/* A read-only field could not explain itself: this branch rendered the label and the value
            and dropped helperText entirely, so a derived value showed as bare text with no hint of
            where it comes from or where to change it. Every editable branch already renders it
            (bug 5716).

            Only rendered when a caller actually supplied one, so the audit fields that pass none are
            unchanged and no layout shifts. */}
        {helperText && (
          <Typography variant="caption" color="text.secondary" display="block">
            {helperText}
          </Typography>
        )}
      </>
    );
  };
  return (
    <>
      {!hidden ? (
        <div className={inputClass}>
          {props.infoIcon && props.onInfoIconClick ? (
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Box flexGrow={1} sx={{ minWidth: 0 }}>
                {isEditingMode ? editableContent() : readOnlyContent()}
              </Box>
              <IconButton
                size="small"
                onClick={props.onInfoIconClick}
                sx={{
                  color: "primary.main",
                  padding: 0.5,
                  alignSelf: "center",
                  mb: 2.5,
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            isEditingMode ? editableContent() : readOnlyContent()
          )}
        </div>
      ) : null}
    </>
  );
};

ViewInputControl.defaultProps = {
  hidden: false,
  isEditingMode: true,
  showLabel: true,
  checked: false,
  dateFormat: DATE_FORMAT.DATE_FORMAT,
  multiple: false,
  onFocus: (fieldKey: string) => null,
  onBlur: (fieldKey: string, value: any) => null,
  onCustomLabelClick: () => null,
  onKeyPress: (e) => null,
  width: "100%",
  onValueChange(value, fieldKey) {},
};

export default observer(ViewInputControl);
